import { supabase } from '../lib/supabase'
import type { User, Session } from '@supabase/supabase-js'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: 'student' | 'teacher' | 'admin' | 'super_admin'
  school_id: string | null
  grade: string | null
  class: string | null
  student_id?: string | null
  password_reset_required?: boolean
  nickname?: string | null
  terms_accepted_at?: string | null
  first_login_completed?: boolean
  attendance_number?: string | null
}

// --- Auth/user caching ---------------------------------------------------
// Collapses the burst of redundant supabase.auth.getUser() and users-profile
// reads that fire on page load (VideosPage + Header + onboarding + announcements
// all ask for the current user at once). Combines a short TTL with in-flight
// promise dedup, and is cleared on any auth state change or profile mutation.
const AUTH_USER_TTL = 30_000 // auth identity is stable within a session
const CURRENT_USER_TTL = 10_000 // profile row can change via edits

let _authUserCache: { user: User | null; ts: number } | null = null
let _authUserInflight: Promise<User | null> | null = null
let _currentUserCache: { user: AuthUser | null; ts: number } | null = null
let _currentUserInflight: Promise<AuthUser | null> | null = null

const clearAuthCaches = (): void => {
  _authUserCache = null
  _authUserInflight = null
  _currentUserCache = null
  _currentUserInflight = null
}

// Bust the caches whenever auth state changes (sign in/out, token refresh,
// user update). Registered once at module load.
supabase.auth.onAuthStateChange(() => {
  clearAuthCaches()
})

export class AuthService {
  // Clear the cached auth user / profile (call after mutating the current
  // user's row so an immediate re-read is never stale).
  static invalidateAuthCache(): void {
    clearAuthCaches()
  }

  // Get the current auth user (cached + deduped wrapper around getUser()).
  static async getAuthUser(): Promise<User | null> {
    if (_authUserCache && Date.now() - _authUserCache.ts < AUTH_USER_TTL) {
      return _authUserCache.user
    }
    if (_authUserInflight) return _authUserInflight
    _authUserInflight = (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        _authUserCache = { user, ts: Date.now() }
        return user
      } finally {
        _authUserInflight = null
      }
    })()
    return _authUserInflight
  }

  // Get current session
  static async getSession(): Promise<{ user: User | null; session: Session | null }> {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    return { user: data.session?.user || null, session: data.session }
  }

  // Check if current user is OAuth user
  static async isOAuthUser(): Promise<boolean> {
    try {
      const user = await this.getAuthUser()
      if (!user) return false

      // Check if user has OAuth provider identities
      const { data: identities } = await supabase.auth.getUserIdentities()
      return identities?.identities?.some(identity => identity.provider === 'google') ?? false
    } catch (error) {
      console.error('Error checking OAuth status:', error)
      return false
    }
  }

  // Get current user profile from database (cached + deduped)
  static async getCurrentUser(): Promise<AuthUser | null> {
    if (_currentUserCache && Date.now() - _currentUserCache.ts < CURRENT_USER_TTL) {
      return _currentUserCache.user
    }
    if (_currentUserInflight) return _currentUserInflight
    _currentUserInflight = (async () => {
      try {
        const user = await this.getAuthUser()
        if (!user) {
          _currentUserCache = { user: null, ts: Date.now() }
          return null
        }

        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('email', user.email)
          .single()

        if (error || !data) {
          _currentUserCache = { user: null, ts: Date.now() }
          return null
        }

        const authUser: AuthUser = {
          id: data.id,
          email: data.email,
          name: data.name,
          role: data.role,
          school_id: data.school_id,
          grade: data.grade,
          class: data.class,
          student_id: data.student_id,
          password_reset_required: data.password_reset_required,
          nickname: data.nickname,
          terms_accepted_at: data.terms_accepted_at,
          first_login_completed: data.first_login_completed,
          attendance_number: data.attendance_number
        }
        _currentUserCache = { user: authUser, ts: Date.now() }
        return authUser
      } finally {
        _currentUserInflight = null
      }
    })()
    return _currentUserInflight
  }

  // Sign in with email and password (for admin users)
  static async signInWithPassword(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    if (error) throw error
    return data
  }

  // Sign in with Google OAuth
  static async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/videos`
      }
    })
    if (error) throw error
    return data
  }

  // Sign out
  static async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    clearAuthCaches()
  }

  // Create a user from the admin dashboard.
  //
  // All role assignment happens server-side in the create-user edge function, which runs
  // with the service role and verifies the caller's own role first. The client never
  // writes users.role, and the signup trigger ignores role metadata (migration 99).
  static async createUser(userData: {
    email: string
    name: string
    role?: 'student' | 'teacher' | 'admin' | 'super_admin'
    school_id?: string
    grade?: string
    class?: string
    password?: string
  }) {
    if (!userData.password) {
      throw new Error('パスワードは必須です')
    }

    const result = await this.createUserViaEdgeFunction({
      email: userData.email,
      name: userData.name,
      password: userData.password,
      role: userData.role || 'student',
      school_id: userData.school_id,
      grade: userData.grade,
      class: userData.class
    })

    if (!result.success) {
      throw new Error(result.error || 'ユーザーの作成に失敗しました')
    }

    return result.user
  }

  // Update user profile
  static async updateUser(userId: string, updates: Partial<AuthUser>) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    clearAuthCaches()
    return data
  }

  // Check if user is admin
  static async isAdmin(): Promise<boolean> {
    const user = await this.getCurrentUser()
    return user?.role === 'admin' || user?.role === 'super_admin'
  }

  // Check if user is super admin
  static async isSuperAdmin(): Promise<boolean> {
    const user = await this.getCurrentUser()
    return user?.role === 'super_admin'
  }

  // Listen to auth state changes
  static onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user || null)
    })
  }

  // Sign in with email and password (for students)
  static async signInWithEmail(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) throw error
    return data
  }

  // Sign in with student ID and password (kept for backward compatibility)
  static async signInWithStudentId(studentId: string, password: string) {
    // First, get the user's email from their student ID
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('email')
      .eq('student_id', studentId)
      .single()

    if (userError || !userData) {
      throw new Error('Student ID not found')
    }

    // Then sign in with email and password
    return this.signInWithEmail(userData.email, password)
  }

  // Create student account with student ID
  static async createStudentAccount(studentData: {
    email: string
    name: string
    password: string
    school_id: string
    grade: string
    class: string
    attendance_number?: string | null
  }) {
    try {
      // First, ensure the current admin user exists in the database
      // This resolves the RLS policy circular dependency issue
      try {
        await this.ensureAdminUserExists()
      } catch (adminError) {
        console.warn('Admin user setup failed, attempting to proceed:', adminError)
        // Try to setup admin account if it doesn't exist
        try {
          await this.setupAdminAccount()
        } catch (setupError) {
          console.warn('Admin account setup failed:', setupError)
          // Continue anyway - might be a bootstrap scenario
        }
      }

      // Check if email already exists
      const { data: existingEmail, error: emailCheckError } = await supabase
        .from('users')
        .select('id')
        .eq('email', studentData.email)
        .maybeSingle()

      if (emailCheckError) {
        throw new Error(`メールアドレスの確認に失敗しました: ${emailCheckError.message}`)
      }

      if (existingEmail) {
        throw new Error(`メールアドレス "${studentData.email}" は既に使用されています`)
      }

      // Create user via Edge Function to avoid session issues
      const userCreationResult = await this.createUserViaEdgeFunction({
        email: studentData.email,
        name: studentData.name,
        password: studentData.password,
        role: 'student',
        school_id: studentData.school_id,
        grade: studentData.grade,
        class: studentData.class,
        attendance_number: studentData.attendance_number,
        password_reset_required: false
      })

      if (!userCreationResult.success) {
        throw new Error(`認証ユーザーの作成に失敗しました: ${userCreationResult.error}`)
      }

      // Edge Function handles complete user creation including database record
      return userCreationResult.user
    } catch (error) {
      console.error('Student account creation error:', error)
      throw error
    }
  }

  // Create teacher account
  static async createTeacherAccount(teacherData: {
    email: string
    name: string
    password: string
    school_id: string
    grade?: string
    class?: string
  }) {
    try {
      // First, ensure the current admin user exists in the database
      try {
        await this.ensureAdminUserExists()
      } catch (adminError) {
        console.warn('Admin user setup failed, attempting to proceed:', adminError)
        // Try to setup admin account if it doesn't exist
        try {
          await this.setupAdminAccount()
        } catch (setupError) {
          console.warn('Admin account setup failed:', setupError)
          // Continue anyway - might be a bootstrap scenario
        }
      }

      // Check if email already exists in users table
      const { data: existingUser, error: userCheckError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', teacherData.email)
        .maybeSingle()

      if (userCheckError) {
        throw new Error(`メールアドレスの確認に失敗しました: ${userCheckError.message}`)
      }
      if (existingUser) {
        throw new Error(`メールアドレス "${teacherData.email}" は既に使用されています`)
      }

      // Also check if email exists in Supabase Auth (admin check)
      try {
        const { data: authUsers, error: authCheckError } = await supabase.auth.admin.listUsers()
        if (authCheckError) {
          console.warn('Could not check auth users (admin privileges may be limited):', authCheckError.message)
        } else if (authUsers?.users?.some(u => u.email === teacherData.email)) {
          throw new Error(`メールアドレス "${teacherData.email}" は既に認証システムに登録されています`)
        }
      } catch (authAdminError) {
        // If admin.listUsers fails, continue - this is a non-critical check
        console.warn('Auth admin check failed:', authAdminError)
      }

      // Create user via Edge Function to avoid session issues
      const userCreationResult = await this.createUserViaEdgeFunction({
        email: teacherData.email,
        name: teacherData.name,
        password: teacherData.password,
        role: 'teacher',
        school_id: teacherData.school_id,
        grade: teacherData.grade,
        class: teacherData.class,
        password_reset_required: false
      })

      if (!userCreationResult.success) {
        throw new Error(`認証ユーザーの作成に失敗しました: ${userCreationResult.error}`)
      }

      // Edge Function handles complete user creation including database record
      return userCreationResult.user
    } catch (error) {
      console.error('Teacher account creation error:', error)
      throw error
    }
  }

  // Check if student ID is available
  static async isStudentIdAvailable(studentId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('student_id', studentId)
      .maybeSingle()

    if (error) throw error
    return !data
  }

  // Reset student password
  static async resetStudentPassword(studentId: string, newPassword: string) {
    // Get the user's email from their student ID
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('email, id')
      .eq('student_id', studentId)
      .single()

    if (userError || !userData) {
      throw new Error('Student ID not found')
    }

    // Update the password in auth
    const { error: authError } = await supabase.auth.admin.updateUserById(
      userData.id, 
      { password: newPassword }
    )
    if (authError) throw authError

    // Mark password reset as required
    const { error: updateError } = await supabase
      .from('users')
      .update({ password_reset_required: true })
      .eq('id', userData.id)

    if (updateError) throw updateError
  }



  // Change password (validates current password first)
  static async changePassword(currentPassword: string, newPassword: string) {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('認証されていません')

    // Validate current password by trying to sign in
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email!,
      password: currentPassword
    })

    if (signInError) {
      throw new Error('現在のパスワードが正しくありません')
    }

    // Update to new password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    if (error) throw error

    // Mark password reset as no longer required
    const currentUser = await this.getCurrentUser()
    if (currentUser) {
      await supabase
        .from('users')
        .update({ password_reset_required: false })
        .eq('id', currentUser.id)
      clearAuthCaches()
    }
  }

  // Create user via Edge Function (admin function)
  static async createUserViaEdgeFunction(userData: {
    email: string
    name: string
    password: string
    role: 'student' | 'teacher' | 'admin' | 'super_admin'
    school_id?: string
    grade?: string
    class?: string
    attendance_number?: string | null
    password_reset_required?: boolean
  }): Promise<{success: boolean, user?: any, error?: string}> {
    try {
      // Get current session token
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('認証が必要です')
      }

      // Call Edge Function
      const { supabaseUrl } = await import('../lib/supabase')
      const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || `サーバーエラーが発生しました（status: ${response.status}）`)
      }

      return result
    } catch (error) {
      console.error('Edge Function call failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Edge Function呼び出しに失敗しました'
      }
    }
  }

  // Set user password by ID (admin function)
  static async setUserPassword(userId: string, newPassword: string) {
    const session = await supabase.auth.getSession()
    if (!session.data.session) {
      throw new Error('認証が必要です')
    }

    const { data, error } = await supabase.functions.invoke('update-password', {
      body: { userId, newPassword },
      headers: {
        Authorization: `Bearer ${session.data.session.access_token}`
      }
    })

    if (error) {
      console.log("error", error)
      throw new Error('Edge Function呼び出しエラー: ' + error.message)
    }

    if (!data.success) {
      throw new Error(data.error || 'パスワードの更新に失敗しました')
    }
  }

  // Update admin password
  static async updateAdminPassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    if (error) throw error
  }

  // Setup admin account for current auth user
  static async setupAdminAccount() {
    try {
      const { data, error } = await supabase.rpc('setup_admin_account')
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Admin setup failed:', error)
      throw error
    }
  }

  // Check admin setup status
  static async checkAdminSetupStatus() {
    try {
      const { data, error } = await supabase
        .from('admin_setup_status')
        .select('*')
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Failed to check admin setup status:', error)
      throw error
    }
  }

  // Ensure current user is set up as admin (for bootstrap)
  static async ensureAdminUserExists() {
    try {
      const { data, error } = await supabase.rpc('ensure_admin_user_exists')
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Failed to ensure admin user exists:', error)
      throw error
    }
  }

  // Bootstrap admin user (for initial setup)
  static async bootstrapAdminUser(email: string, name: string, role: 'admin' | 'super_admin' = 'admin') {
    try {
      const { data, error } = await supabase.rpc('bootstrap_admin_user', {
        p_email: email,
        p_name: name,
        p_role: role
      })
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Failed to bootstrap admin user:', error)
      throw error
    }
  }

  // Log login attempt
  static async logLoginAttempt(
    email?: string,
    studentId?: string,
    success: boolean = false,
    failureReason?: string
  ) {
    try {
      // Use Edge Function instead of RPC
      const { error } = await supabase.functions.invoke('log-login-attempt', {
        body: {
          email: email || undefined,
          student_id: studentId || undefined,
          success: success,
          failure_reason: failureReason || undefined
        }
      })

      if (error) {
        console.error('Failed to log login attempt:', error)
      }
    } catch (err) {
      console.error('Error calling log-login-attempt function:', err)
    }
  }

  // Manually confirm a user account (backup method)
  static async confirmUserAccount(email: string) {
    const { error } = await supabase.rpc('confirm_user_account', {
      user_email: email
    })
    
    if (error) throw error
  }

  // Bulk confirm recent admin-created users
  static async confirmRecentAdminUsers(): Promise<number> {
    const { data, error } = await supabase.rpc('confirm_recent_admin_users')
    
    if (error) throw error
    return data || 0
  }

  // Confirm all admin users (useful for suppressing confirmation emails)
  static async confirmAllAdminUsers(): Promise<number> {
    const { data, error } = await supabase.rpc('confirm_all_admin_users')
    if (error) throw error
    return data || 0
  }

  // Log admin action
  static async logAdminAction(
    action: string,
    targetType?: string,
    targetId?: string,
    details?: Record<string, unknown>
  ) {
    const { data, error } = await supabase.rpc('log_admin_action', {
      p_action: action,
      p_target_type: targetType,
      p_target_id: targetId,
      p_details: details
    })

    if (error) throw error
    return data
  }

  // Check if user needs to complete first-time onboarding
  static async needsOnboarding(user?: AuthUser): Promise<boolean> {
    try {
      const currentUser = user || await this.getCurrentUser()
      if (!currentUser) return false

      // Check if user has completed first-time onboarding
      return !currentUser.first_login_completed
    } catch (error) {
      console.error('Error checking onboarding status:', error)
      return false
    }
  }

  // Check if user needs password change (for email/password users)
  static async needsPasswordChange(user?: AuthUser): Promise<boolean> {
    try {
      const currentUser = user || await this.getCurrentUser()
      if (!currentUser) return false

      // Check if password reset is required
      return !!currentUser.password_reset_required
    } catch (error) {
      console.error('Error checking password change status:', error)
      return false
    }
  }

  // Check the onboarding flow status for email/password users
  static async getOnboardingFlowStatus(user?: AuthUser): Promise<{
    needsPasswordChange: boolean
    needsOnboarding: boolean
    isOAuthUser: boolean
  }> {
    try {
      const currentUser = user || await this.getCurrentUser()
      if (!currentUser) {
        return { needsPasswordChange: false, needsOnboarding: false, isOAuthUser: false }
      }

      const isOAuthUser = await this.isOAuthUser()
      const needsPasswordChange = !isOAuthUser && !!currentUser.password_reset_required
      const needsOnboarding = !currentUser.first_login_completed

      return {
        needsPasswordChange,
        needsOnboarding,
        isOAuthUser
      }
    } catch (error) {
      console.error('Error checking onboarding flow status:', error)
      return { needsPasswordChange: false, needsOnboarding: false, isOAuthUser: false }
    }
  }

  // Complete first-time onboarding
  static async completeFirstTimeOnboarding(nickname?: string): Promise<void> {
    try {
      const currentUser = await this.getCurrentUser()
      if (!currentUser) {
        throw new Error('ユーザーが見つかりません')
      }

      const updateData: {
        terms_accepted_at: string
        first_login_completed: boolean
        updated_at: string
        nickname?: string
      } = {
        terms_accepted_at: new Date().toISOString(),
        first_login_completed: true,
        updated_at: new Date().toISOString()
      }

      // Only set nickname if provided
      if (nickname && nickname.trim()) {
        updateData.nickname = nickname.trim()
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', currentUser.id)

      if (error) {
        console.error('Failed to complete onboarding:', error)
        throw new Error('設定の保存に失敗しました')
      }

      clearAuthCaches()
      console.log('First-time onboarding completed successfully')
    } catch (error) {
      console.error('Error completing onboarding:', error)
      throw error
    }
  }

  // Request password reset email
  static async requestPasswordReset(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) throw error
  }

  // Get user's display name (nickname or name)
  static getDisplayName(user: AuthUser): string {
    return user.nickname || user.name || 'ユーザー'
  }
}